/**
 * Official profile · Timeline: the same events timeline as the public ward page's Timeline tab (site/js/app.js
 * renderTimeline; scripts/entities.py → GET /api/timeline?member=<ward>): donations, lobbyist gifts, lobbying,
 * legislation they introduced and how they voted on divided roll calls, newest first, by month, a page at a time.
 * Same filters, labels and source note as the public page.
 */
import { useEffect, useState } from "react";
import { List, ListRow, RowLead, RowMain, RowMeta, RowSub, RowTitle } from "../components/shared/List";
import { mdy, money } from "./atoms";

const GROUPS = {
  all: { label: "Everything", kinds: [] as string[] },
  money: { label: "Money", kinds: ["contribution", "lobbyist_contribution", "gift"] },
  lobbying: { label: "Lobbying", kinds: ["lobbying", "lobbied_on"] },
  legislation: { label: "Legislation", kinds: ["introduced", "roll_call"] },
  votes: { label: "Divided votes", kinds: ["vote_yes", "vote_no"] },
};
type Group = keyof typeof GROUPS;
const KIND: Record<string, [string, string]> = {
  contribution: ["Donation", "neutral"], lobbyist_contribution: ["Lobbyist donation", "warning"], gift: ["Gift", "warning"],
  lobbying: ["Lobbying", "neutral"], lobbied_on: ["Lobbied on a bill", "neutral"], introduced: ["Submitted", "neutral"],
  roll_call: ["Roll call", "neutral"], vote_yes: ["Voted yes", "positive"], vote_no: ["Voted no", "negative"],
};

interface Party { id: string; name?: string }
interface TimelineEvent {
  kind: string; date: string; title?: string; amount?: number | null;
  actor?: Party | null; target?: Party | null; matter?: { id: string } | null; detail?: { department?: string } | null;
}

const entityHref = (id: string) => `/entity/${encodeURIComponent(id).replace("%3A", ":")}`;
const monthOf = (d: string) => new Date(`${d.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const titleCase = (s: string) => s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());

export function TimelineTab({ ward, name }: { ward: number; name: string }) {
  const [group, setGroup] = useState<Group>("all");
  const [items, setItems] = useState<TimelineEvent[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (reset: boolean, from: string | null, g: Group, signal?: AbortSignal) => {
    const q = new URLSearchParams({ member: String(ward), limit: "40" });
    if (GROUPS[g].kinds.length) q.set("kind", GROUPS[g].kinds.join(","));
    if (from) q.set("cursor", from);
    setLoading(true);
    const res = await fetch(`/api/timeline?${q}`, { credentials: "include", signal }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (signal?.aborted) return;
    setLoading(false);
    if (!res) { setFailed(true); return; }
    setFailed(false);
    setItems((prev) => (reset ? res.data : [...(prev ?? []), ...res.data]));
    setCursor(res.meta?.nextCursor ?? null);
  };
  useEffect(() => {
    const ac = new AbortController();
    setItems(null); setCursor(null);
    void load(true, null, group, ac.signal);
    return () => ac.abort();
  }, [ward, group]); // eslint-disable-line react-hooks/exhaustive-deps

  const who = (x?: Party | null) => (x && x.name !== name ? <a href={entityHref(x.id)}>{x.name || x.id}</a> : null);
  const rows: React.ReactNode[] = [];
  let month = "";
  for (const [i, e] of (items ?? []).entries()) {
    const m = monthOf(e.date);
    if (m !== month) { month = m; rows.push(<h3 key={`m${i}`} className="p-tl-month">{m}</h3>); }
    const [label, tone] = KIND[e.kind] ?? [e.kind, "neutral"];
    const links = [who(e.actor), who(e.target), e.matter ? <a href={`/dashboard/legislation/${encodeURIComponent(e.matter.id)}`}>Legislation →</a> : null].filter(Boolean);
    rows.push(
      <List key={`e${i}`} className="p-tl-list">
        <ListRow className="p-tl-row">
          <RowLead className="p-tl-date">{mdy(e.date, { month: "short", day: "numeric" })}</RowLead>
          <RowMain min={160}>
            <RowMeta><span className={`chip chip--${tone}`}>{label}</span>{e.amount ? <strong>{money(e.amount)}</strong> : null}</RowMeta>
            <RowTitle lines={2}>{e.title ?? ""}</RowTitle>
            {e.detail?.department && <RowSub>{titleCase(e.detail.department)}</RowSub>}
            {links.length > 0 && <RowSub>{links.map((l, j) => <span key={j}>{j > 0 && " · "}{l}</span>)}</RowSub>}
          </RowMain>
        </ListRow>
      </List>,
    );
  }

  return (
    <div className="p-cols">
      <div className="p-main p-main--32">
        <section>
          <div className="p-money-head">
            <h2>Timeline</h2>
            <div className="p-seg p-seg--right" role="radiogroup" aria-label="Show">
              {(Object.keys(GROUPS) as Group[]).map((g) => (
                <button key={g} role="radio" aria-checked={group === g} onClick={() => setGroup(g)}>{GROUPS[g].label}</button>
              ))}
            </div>
          </div>
          <p className="muted small">Donations to their campaign, lobbyist gifts, legislation they introduced, and how they voted on divided roll calls, newest first.</p>
          {failed && !items?.length ? <p className="muted">The timeline isn't available right now.</p>
            : items === null ? null
            : items.length === 0 ? <p className="muted">Nothing here yet.</p>
            : <div className="p-tl">{rows}</div>}
          {cursor && <button className="btn p-tl-more" disabled={loading} aria-busy={loading || undefined} onClick={() => void load(false, cursor, group)}>Show older</button>}
          <p className="small muted">From public records: State campaign finance filings, City lobbying reports, and Council votes and legislation. People with the same name can be merged. Connections are not evidence of wrongdoing.</p>
        </section>
      </div>
    </div>
  );
}
